/**
 * FAQ Page
 * Premium FAQ page with categorized, searchable questions
 */

import { useState, useMemo } from 'react';
import { Layout } from '~/components/layout/Layout';
import { motion, AnimatePresence } from 'motion/react';
import {
  IconSearch,
  IconChevronDown,
  IconHeadset,
  IconCart,
  IconArrowRight,
} from '~/components/ui/Icons';
import { faqContent } from '~/lib/cms/content';

// Settings icon
function IconSettings({ size = 24, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

export function FAQPage() {
  const content = faqContent;
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
    cart: IconCart,
    headset: IconHeadset,
    settings: IconSettings,
  };

  // Filter FAQs based on search
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return content.categories;

    const query = searchQuery.toLowerCase();
    return content.categories
      .map((category) => ({
        ...category,
        questions: category.questions.filter(
          (q) =>
            q.q.toLowerCase().includes(query) ||
            q.a.toLowerCase().includes(query)
        ),
      }))
      .filter((category) => category.questions.length > 0);
  }, [searchQuery, content.categories]);

  const totalQuestions = content.categories.reduce(
    (acc, cat) => acc + cat.questions.length,
    0
  );

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-surface-coal via-surface-black to-surface-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,116,0,0.08),transparent_60%)]" />

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="container-wide relative py-20 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-orange/10 border border-brand-orange/20 rounded-full mb-6">
              <span className="text-sm font-medium text-brand-orange">
                {totalQuestions} Questions Answered
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-accent-white mb-6">
              {content.hero.title}
            </h1>

            <p className="text-xl text-accent-silver mb-10">
              {content.hero.subtitle}
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <div className="absolute inset-0 bg-gradient-to-r from-brand-orange/20 to-purple-500/20 rounded-2xl blur-xl opacity-50" />
              <div className="relative flex items-center bg-surface-coal/80 backdrop-blur-xl border border-surface-slate/50 rounded-2xl overflow-hidden">
                <IconSearch size={20} className="text-accent-silver ml-5" />
                <input
                  type="text"
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent px-4 py-4 text-accent-white placeholder:text-accent-silver/50 focus:outline-none"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mr-4 text-accent-silver hover:text-accent-white transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Pills */}
      <section className="container-wide py-8">
        <div className="flex flex-wrap justify-center gap-3">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
              activeCategory === null
                ? 'bg-brand-orange text-white shadow-lg shadow-brand-orange/25'
                : 'bg-surface-coal text-accent-silver hover:text-accent-white border border-surface-slate/30'
            }`}
          >
            All Categories
          </button>
          {content.categories.map((category) => {
            const Icon = iconMap[category.icon] || IconHeadset;
            return (
              <button
                key={category.name}
                onClick={() => setActiveCategory(category.name)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                  activeCategory === category.name
                    ? 'bg-brand-orange text-white shadow-lg shadow-brand-orange/25'
                    : 'bg-surface-coal text-accent-silver hover:text-accent-white border border-surface-slate/30'
                }`}
              >
                <Icon size={16} />
                {category.name}
              </button>
            );
          })}
        </div>
      </section>

      {/* FAQ Content */}
      <section className="container-wide py-12 pb-24">
        {searchQuery && filteredCategories.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 bg-surface-coal rounded-2xl flex items-center justify-center mx-auto mb-6">
              <IconSearch size={32} className="text-accent-silver" />
            </div>
            <h3 className="text-xl font-semibold text-accent-white mb-2">
              No results found
            </h3>
            <p className="text-accent-silver mb-6">
              Try a different search term or browse categories
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="text-brand-orange hover:text-brand-orange-light font-medium"
            >
              Clear search
            </button>
          </motion.div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-12">
            {filteredCategories
              .filter(
                (category) =>
                  !activeCategory || category.name === activeCategory
              )
              .map((category, categoryIndex) => {
                const Icon = iconMap[category.icon] || IconHeadset;

                return (
                  <motion.div
                    key={category.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
                  >
                    {/* Category Header */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-brand-orange/10 flex items-center justify-center">
                        <Icon size={24} className="text-brand-orange" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-display font-bold text-accent-white">
                          {category.name}
                        </h2>
                        <p className="text-sm text-accent-silver">
                          {category.questions.length} questions
                        </p>
                      </div>
                    </div>

                    {/* Questions */}
                    <div className="space-y-3">
                      {category.questions.map((faq, faqIndex) => {
                        const faqKey = `${category.name}-${faqIndex}`;
                        const isOpen = openFaq === faqKey;

                        return (
                          <div
                            key={faqIndex}
                            className="rounded-xl bg-surface-coal border border-surface-slate/30 overflow-hidden"
                          >
                            <button
                              onClick={() =>
                                setOpenFaq(isOpen ? null : faqKey)
                              }
                              className="w-full flex items-center justify-between p-5 text-left hover:bg-surface-graphite/30 transition-colors"
                            >
                              <span className="text-lg font-medium text-accent-white pr-4">
                                {faq.q}
                              </span>
                              <motion.div
                                animate={{ rotate: isOpen ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                                className="flex-shrink-0 w-8 h-8 rounded-lg bg-surface-graphite flex items-center justify-center"
                              >
                                <IconChevronDown
                                  size={18}
                                  className="text-accent-silver"
                                />
                              </motion.div>
                            </button>

                            <AnimatePresence>
                              {isOpen && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  <div className="px-5 pb-5 text-accent-silver leading-relaxed border-t border-surface-slate/20 pt-4">
                                    {faq.a}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              })}
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="container-wide pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-orange via-brand-orange to-brand-orange-dark p-10 md:p-16 text-center"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]" />

          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
              Still have questions?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-gray-100 text-brand-orange font-semibold rounded-xl transition-colors shadow-lg"
              >
                Contact Support
                <IconArrowRight size={18} />
              </a>
              <a
                href="/support"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-colors border border-white/20"
              >
                Browse Help Centre
              </a>
            </div>
          </div>
        </motion.div>
      </section>
    </Layout>
  );
}
